import { Step, StepLabel, Stepper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { INCOME_OR_EXPENSE_LIST } from 'adminConstants/routes';
import ErrorBoundary from 'components/ErrorBoundary';
import Page from 'components/new/Page';
import PageLoader from 'components/PageLoader';
import { Column, Row } from 'further-ui/components/Layout';
import { useLayoutContext } from 'contexts/LayoutContext';
import { IncomeOrExpenseType } from 'further-types/lp/income-and-expenses';
import api from 'lib/trpcClient';
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { AiFormFillProvider } from './AiFormFillContext';
import FormWrapper from './FormWrapper';
import InvoicePreview from './InvoicePreview';
import StepAddAndAllocate from './StepAddAndAllocate';
import StepReviewAndConfirm from './StepReviewAndConfirm';

enum Steps {
  AddAndAllocate,
  ReviewAndConfirm,
}

const stepLabels = {
  [Steps.AddAndAllocate]: 'Add and allocate',
  [Steps.ReviewAndConfirm]: 'Review and confirm',
};

type Props = {
  vehicleId: string;
};

const StepperWrapper = styled('div')({
  width: 460,
});

const PageLayout = styled(Row)(({ theme }) => ({
  height: `calc(100vh - ${theme.searchBar.height + 2}px)`,
}));

const FormColumn = styled(Column, {
  shouldForwardProp: (prop) => prop !== 'hasPreview',
})<{ hasPreview: boolean }>(({ hasPreview }) => ({
  flex: hasPreview ? '7 7 0%' : '1 1 auto',
  minWidth: 0,
}));

const PreviewColumn = styled(Column)(({ theme }) => ({
  flex: '5 5 0%',
  height: `calc(100vh - ${theme.searchBar.height + 2}px)`,
  backgroundColor: theme.palette.grey[200],
  overflow: 'hidden',
  padding: theme.spacing(12),
}));

type PreviewData = {
  fileUrl?: string | null;
  fileName?: string | null;
  isUploading: boolean;
} | null;

const AddEditIncomeOrExpense: React.FC<Props> = ({ vehicleId }) => {
  const { id, type } = useParams<{ id?: string; type?: IncomeOrExpenseType }>();
  const { vehicle } = useLayoutContext();
  const location = useLocation();
  const isSwitchMode =
    new URLSearchParams(location.search).get('mode') === 'switch';
  const [activeStep, setActiveStep] = useState<Steps>(Steps.AddAndAllocate);
  const [previewData, setPreviewData] = useState<PreviewData>(null);
  const hasPreview = previewData !== null;

  const { data: incomeOrExpense, isLoading: incomeOrExpenseLoading } =
    api.incomeAndExpenses.get.useQuery(
      { vehicleId, id: id ?? '' },
      { enabled: !!id },
    );

  // When editing a record that already has an invoice file, show preview immediately
  useEffect(() => {
    if (incomeOrExpense?.invoiceFile) {
      setPreviewData({
        fileUrl: incomeOrExpense.invoiceFile.preview,
        fileName: incomeOrExpense.invoiceFile.name,
        isUploading: false,
      });
    }
  }, [incomeOrExpense?.invoiceFile]);

  if (incomeOrExpenseLoading) {
    return <PageLoader />;
  }

  const title = isSwitchMode
    ? 'Switch to payable or receivable'
    : id
    ? 'Edit income or expense'
    : 'Add new income or expense';

  const stepper = (
    <Stepper activeStep={activeStep}>
      {Object.values(stepLabels).map((stepLabel) => (
        <Step key={stepLabel}>
          <StepLabel>{stepLabel}</StepLabel>
        </Step>
      ))}
    </Stepper>
  );

  const page = (
    <Page
      tabTitle="Income & Expenses: Add Income or Expense"
      heading={title}
      goBackAction={INCOME_OR_EXPENSE_LIST}
      spacing="lg"
      withHeadingDivider
      customActions={
        hasPreview ? undefined : (
          <Row spacing="sm" centered>
            <StepperWrapper>{stepper}</StepperWrapper>
          </Row>
        )
      }
      belowHeading={hasPreview ? stepper : undefined}
    >
      <FormWrapper
        values={incomeOrExpense}
        vehicleId={vehicleId}
        baseCurrency={vehicle?.baseCurrency}
        defaultCategory={type}
        isSwitchMode={isSwitchMode}
      >
        <AiFormFillProvider>
          {activeStep === Steps.AddAndAllocate && (
            <StepAddAndAllocate
              id={id}
              isSwitchMode={isSwitchMode}
              onPreviewDataChange={setPreviewData}
              onNext={() => {
                setActiveStep(Steps.ReviewAndConfirm);
              }}
            />
          )}
          {activeStep === Steps.ReviewAndConfirm && !!vehicle && (
            <ErrorBoundary>
              <StepReviewAndConfirm
                id={id}
                vehicle={vehicle}
                isSwitchMode={isSwitchMode}
                onPreviousStep={() => {
                  setActiveStep(Steps.AddAndAllocate);
                }}
              />
            </ErrorBoundary>
          )}
        </AiFormFillProvider>
      </FormWrapper>
    </Page>
  );

  return (
    <PageLayout>
      <FormColumn hasPreview={hasPreview}>{page}</FormColumn>
      {hasPreview && (
        <PreviewColumn>
          <InvoicePreview
            fileUrl={previewData?.fileUrl}
            fileName={previewData?.fileName}
            isUploading={previewData?.isUploading}
          />
        </PreviewColumn>
      )}
    </PageLayout>
  );
};

const AddEditIncomeOrExpenseWrapper: React.FC = () => {
  const { selectedVehicleId } = useLayoutContext();

  if (!selectedVehicleId) {
    return null;
  }

  return <AddEditIncomeOrExpense vehicleId={selectedVehicleId} />;
};

export default AddEditIncomeOrExpenseWrapper;
